import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { DataContext } from './DataContext';
import { _onRenderNavLink } from './ZedXMainMenu';
import { TruncatedText } from './TruncatedText';
import { IEntityDescription1 } from '../types';

interface IManageProjectsMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    entityDescriptionForMenu?: IEntityDescription1;

    // Left-nav expand/collapse state, lifted up to ManageProjectsApp so it
    // survives this component remounting (e.g. on the mobile drawer toggle).
    manageNavExpanded: boolean;
    setManageNavExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    settingsNavExpanded: boolean;
    setSettingsNavExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    financialsNavExpanded: boolean;
    setFinancialsNavExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    hasInitialisedNavFromRoute: boolean;
    setHasInitialisedNavFromRoute: React.Dispatch<React.SetStateAction<boolean>>;
}

type ContextualInfo = {
    isContextualMenu: boolean;
    selectedLabel?: string;
};

// Identifies which expand/collapse setter a given composite-link header
// key maps to. Keys are formatted as `${headerKey}-${expandedFlag}`.
type HeaderKey = 'manage' | 'financials' | 'settings';

export const ManageProjectsMenu = ({
    entityDescriptionForMenu,
    errorHandling: { clearErrors },
    onLinkClicked,
    manageNavExpanded,
    setManageNavExpanded,
    settingsNavExpanded,
    setSettingsNavExpanded,
    financialsNavExpanded,
    setFinancialsNavExpanded,
    hasInitialisedNavFromRoute,
    setHasInitialisedNavFromRoute
}: IManageProjectsMenu): React.ReactElement => {

    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();
    const { dataServices } = useContext(DataContext);

    const [navs, setNavs] = useState<INavLink[]>([]);

    const [totalPortfolios, setTotalPortfolios] = useState(0);
    const [totalProgrammes, setTotalProgrammes] = useState(0);
    const [totalProjects, setTotalProjects] = useState(0);

    const resetErrors = useCallback(clearErrors, [clearErrors]);

    /* -------------------------------------------------------
       Parse contextual menu mode
    -------------------------------------------------------- */
    const parseContextualMenu = (path: string): ContextualInfo => {

        if (/^\/a2\/objectives-for-vision\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Objectives' };
        }

        if (/^\/a2\/portfolios-for-objective\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Portfolios' };
        }

        if (/^\/a2\/programmes-for-portfolio\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Programmes' };
        }

        if (
            /^\/a2\/projects-for-portfolio\/[^/]+/.test(path) ||
            /^\/a2\/projects-for-programme\/[^/]+/.test(path)
        ) {
            return { isContextualMenu: true, selectedLabel: 'Projects' };
        }

        if (/^\/a2\/sign-off-for-portfolio\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Portfolio Updates' };
        }

        if (/^\/a2\/sign-off-for-programme\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Programme Updates' };
        }

        if (/^\/a2\/sign-off-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Project Updates' };
        }

        if (/^\/a2\/work-streams-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Work streams' };
        }

        if (/^\/a2\/benefits-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Benefits' };
        }

        if (/^\/a2\/dependencies-for-project\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Dependencies' };
        }

        if (/^\/a2\/milestones-for-work-stream\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Milestones' };
        }

        // ---- FINANCIALS: drill-down routes ----

        if (/^\/a2\/financials\/programmes-for-portfolio\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Financials - Programmes' };
        }

        if (
            /^\/a2\/financials\/projects-for-portfolio\/[^/]+/.test(path) ||
            /^\/a2\/financials\/projects-for-programme\/[^/]+/.test(path)
        ) {
            return { isContextualMenu: true, selectedLabel: 'Financials - Projects' };
        }

        return { isContextualMenu: false };
    };

    const { isContextualMenu, selectedLabel } = parseContextualMenu(pathname);

    /* -------------------------------------------------------
       Fetch counters
    -------------------------------------------------------- */
    useEffect(() => {
        dataServices.portfolioService.countPortfolios().then(setTotalPortfolios);
        dataServices.programmeService.countProgrammes().then(setTotalProgrammes);
        dataServices.projectService.countProjects().then(setTotalProjects);
    }, [dataServices]);

    /* -------------------------------------------------------
       One-time auto-expand based on initial route
       (Manage stays open regardless)
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedNavFromRoute || isContextualMenu) return;

        if (
            pathname.startsWith('/a2/settings/')
        ) {
            setSettingsNavExpanded(true);
        }

        if (
            pathname.startsWith('/a2/financials/')
        ) {
            setFinancialsNavExpanded(true);
        }

        setHasInitialisedNavFromRoute(true);
    }, [pathname, isContextualMenu, hasInitialisedNavFromRoute, setSettingsNavExpanded, setFinancialsNavExpanded, setHasInitialisedNavFromRoute]);

    /* -------------------------------------------------------
       Chevron/expand click handler for the Manage / Financials /
       Settings composite-link headers. Fluent's Nav routes BOTH
       the chevron icon and (on some platforms) the row body
       through this single callback for expandable links, so this
       is the one place expand/collapse state is set — no separate
       per-header onClick needed.
    -------------------------------------------------------- */
    const onLinkExpandClick = useCallback((ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
        ev?.preventDefault();
        const headerKey = (item?.key?.split('-')[0]) as HeaderKey | undefined;
        if (!headerKey) return;

        const target = !item?.isExpanded;
        switch (headerKey) {
            case 'manage':
                setManageNavExpanded(target);
                break;
            case 'financials':
                setFinancialsNavExpanded(target);
                break;
            case 'settings':
                setSettingsNavExpanded(target);
                break;
        }
    }, [setManageNavExpanded, setFinancialsNavExpanded, setSettingsNavExpanded]);

    /* -------------------------------------------------------
       Build navigation
    -------------------------------------------------------- */
    useEffect(() => {
        const routerNav = (path: string): void => {
            resetErrors();
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        /* -------- CONTEXTUAL MENU -------- */
        if (isContextualMenu && selectedLabel) {
            const links: INavLink[] = [
                { name: 'Home', path: '/' },
                { name: 'Welcome', path: '/a2/welcome' },
                { name: selectedLabel, path: pathname }
            ].map(NamePathToNavLink);

            setNavs(links);
            return;
        }

        /* -------- GLOBAL MENU -------- */
        const links: INavLink[] = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a2/welcome' },
            { name: 'Insights', path: '/a2/insights' },
            { name: 'Ask Cloe', path: '/a2/ai-chats' },
            { name: 'Vision', path: '/a2/visions' },
            { name: 'Objectives', path: '/a2/objectives' }
        ].map(NamePathToNavLink);

        const canViewProjects = up.UserCanViewProjectEntities();
        const canViewDirectorate = up.UserCanViewDirectorateEntities();

        if (up.UserIsSystemAdmin() || canViewProjects || canViewDirectorate) {
            links.push({
                key: `manage-${manageNavExpanded}`,
                name: 'Manage',
                url: null,
                isExpanded: manageNavExpanded,
                links: [
                    canViewDirectorate && { name: 'Portfolios', path: '/a2/portfolios' },
                    canViewDirectorate && { name: 'Programmes', path: '/a2/programmes' },
                    canViewProjects && { name: 'Projects', path: '/a2/projects' }
                ].filter(Boolean).map(NamePathToNavLink)
            });

            links.push({
                key: `financials-${financialsNavExpanded}`,
                name: 'Financials',
                url: null,
                isExpanded: financialsNavExpanded,
                links: [
                    canViewDirectorate && { name: 'Portfolios', path: '/a2/financials/portfolios' },
                    canViewDirectorate && { name: 'Programmes', path: '/a2/financials/programmes' },
                    canViewProjects && { name: 'Projects', path: '/a2/financials/projects' }
                ].filter(Boolean).map(NamePathToNavLink)
            });
        }

        if (up.UserIsSystemAdmin()) {
            links.push({
                key: `settings-${settingsNavExpanded}`,
                name: 'Settings',
                url: null,
                isExpanded: settingsNavExpanded,
                links: [
                    { name: 'Benefit types', path: '/a2/settings/benefit-types' },
                    { name: 'Reporting frequencies', path: '/a2/settings/reporting-frequencies' },
                    { name: 'Risk types', path: '/a2/settings/risk-types' },
                    { name: 'Risk discussion forums', path: '/a2/settings/risk-discussion-forums' },
                    { name: 'Units', path: '/a2/settings/units' },
                    { name: 'Thresholds', path: '/a2/settings/thresholds' },
                    { name: 'Threshold appetites', path: '/a2/settings/threshold-appetites' },
                    { name: 'Financial Year', path: '/a2/settings/update-financial-year' }
                ].map(NamePathToNavLink)
            });
        }

        links.push(NamePathToNavLink({ name: 'Outputs', path: '/a2/outputs' }));

        setNavs(links);
    }, [
        up,
        history,
        onLinkClicked,
        resetErrors,
        pathname,
        isContextualMenu,
        selectedLabel,
        manageNavExpanded,
        settingsNavExpanded,
        financialsNavExpanded
    ]);

    /* -------------------------------------------------------
       Render
    -------------------------------------------------------- */
    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: 0,
                        paddingBottom: '1px',
                        paddingLeft: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-ManageProjects.png`}
                        alt="Manage Projects"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Contextual header */}
                {isContextualMenu && entityDescriptionForMenu && (
                    <div
                        style={{
                            backgroundColor: '#1A619F',
                            color: 'white',
                            padding: '10px 15px',
                            margin: '5px 10px 10px',
                            borderRadius: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                        }}
                    >
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                {/* Navigation */}
                <Nav
                    selectedKey={pathname}
                    groups={[{ links: navs }]}
                    onLinkExpandClick={onLinkExpandClick}
                    onRenderLink={(lnk) =>
                        _onRenderNavLink(
                            lnk,
                            totalPortfolios,
                            totalProgrammes,
                            totalProjects
                        )
                    }
                />
            </div>
        </ThemeProvider>
    );
};